import type { EmployeeForStaffingCalendar } from './employee-for-staffing-calendar';
import { LocalDate } from '../../../utilities/local-date';
import { timekeepingPeriod } from './timekeeping-period';
import type { LeavePeriod, PlusTimePeriod } from '@trinnvis/dabih-api-client';
import { TimekeepingCorrectionCorrectionTypeEnum } from '@trinnvis/dabih-api-client';
import { orderBy } from 'lodash';
import type { TimekeepingPeriod } from './timekeeping-period-type';

/**
 * Takes an employee and a list of holidays
 * Returns a list of time keeping periods with data for display
 */
export function timekeepingPeriods(
  employees: EmployeeForStaffingCalendar[],
  employeeUuid: string,
  holidays: { [p: string]: string },
) {
  if (employees && employeeUuid) {
    const timeKeepingStartDate = LocalDate.fromString('2021-01-01');
    const timekeepingPeriods: TimekeepingPeriod[] = [];
    let timeOffPeriods: LeavePeriod[] = [];
    let plusTimePeriods: PlusTimePeriod[] = [];
    const employee = employees.filter((item) => {
      return item.uuid === employeeUuid;
    })[0];
    if (employee === undefined) {
      return [];
    }
    if (employee.plusTimePeriods) {
      plusTimePeriods = employee.plusTimePeriods.filter((item) => {
        const start = LocalDate.fromString(item.start);
        const end = LocalDate.fromString(item.end);
        return start.isSameOrAfter(timeKeepingStartDate) && end.isSameOrAfter(timeKeepingStartDate);
      });
    }
    const leavePeriods = employee.leavePeriods;
    if (leavePeriods.length) {
      timeOffPeriods = leavePeriods.filter((item) => {
        const start = LocalDate.fromString(item.start);
        const end = LocalDate.fromString(item.end);
        return (
          item.type === 'timeOff' &&
          start.isSameOrAfter(timeKeepingStartDate) &&
          end.isSameOrAfter(timeKeepingStartDate)
        );
      });
    }
    timeOffPeriods.forEach((period) => {
      timekeepingPeriods.push(timekeepingPeriod('timeOff', period, employee, holidays));
    });
    plusTimePeriods.forEach((period) => {
      timekeepingPeriods.push(timekeepingPeriod('plusTime', period, employee, holidays));
    });
    if (employee.timekeepingCorrections) {
      employee.timekeepingCorrections.forEach(function (correction) {
        let type = 'correction';
        let prefix = '- ';
        if (correction.correctionType === TimekeepingCorrectionCorrectionTypeEnum.Plus) {
          type = 'correctionPlus';
          prefix = '+ ';
        }
        const hoursDisplay = prefix + correction.hours.toFixed(2).replace('.', ',') + ' t';
        timekeepingPeriods.push({
          type: type,
          confirmed: true,
          start: correction.date,
          startTime: '',
          end: correction.date,
          endTime: '',
          timeDisplay: LocalDate.fromString(correction.date).toStringForDisplayWithDayOfWeekAndYear() + ': Avregning',
          minutes: correction.hours * 60,
          hoursDisplay: hoursDisplay,
          notes: correction.notes,
          id: correction.uuid,
        });
      });
    }
    return orderBy(timekeepingPeriods, [(e) => e.start], ['desc']);
  }
  return [];
}
